// Component for handling file uploads through drag and drop
import React, { useState, useCallback } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

const DropzoneBox = styled(Box)(({ theme }) => ({
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  textAlign: 'center',
  cursor: 'pointer',
  backgroundColor: theme.palette.background.default,
  transition: 'border-color 0.2s ease-in-out',
  '&:hover': {
    borderColor: theme.palette.primary.main
  }
}));

const FileUploader = ({ onHtmlContent }: { onHtmlContent: (content: string) => void }) => {
  const [fileName, setFileName] = useState('');
  const [uploadError, setUploadError] = useState('');

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setUploadError('');
      const file = acceptedFiles[0];

      if (!file) return;

      if (!file.name.endsWith('.html')) {
        setUploadError('Only HTML files are supported');
        return;
      }

      setFileName(file.name);

      const reader = new FileReader();
      reader.onload = () => {
        const content = reader.result;
        onHtmlContent(content as string);
      };

      reader.readAsText(file);
    },
    [onHtmlContent]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    // accept: {
    //   'text/html': ['.html', '.htm']
    // },
    maxFiles: 1
  });

  return (
    <Box>
      <DropzoneBox
        {...getRootProps()}
        sx={{
          borderColor: isDragActive ? 'primary.main' : 'divider',
          mb: 1.5
        }}>
        <input {...getInputProps()} />
        <CloudUploadIcon sx={{ fontSize: 32, color: 'text.secondary', mb: 0.5 }} />
        <Typography variant="body2" color="textSecondary">
          {isDragActive ? 'Drop here' : 'Drop HTML file or click'}
        </Typography>
        {fileName && (
          <Typography variant="caption" sx={{ mt: 0.5, display: 'block', fontWeight: 600 }}>
            {fileName}
          </Typography>
        )}
      </DropzoneBox>

      {uploadError && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 0.5 }}>
          {uploadError}
        </Typography>
      )}

      <Button
        variant="outlined"
        size="small"
        onClick={() => onHtmlContent('')}
        disabled={!fileName}>
        Clear
      </Button>
    </Box>
  );
};

export default FileUploader;
